import {
  Umbrella,
  Receipt,
  Gavel,
  Ship,
  Building2,
  Stamp,
  Landmark,
  FileSignature,
  Home,
  Handshake,
  IdCard,
  Languages,
  Calculator,
  FileCheck2,
  type LucideIcon,
} from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

type Service = { label: string; Icon: LucideIcon };

// Icons stay here, aligned by index with t.services.items in both languages.
const icons: LucideIcon[] = [
  Umbrella, // Retirement & Social Security
  Receipt, // Taxes
  Gavel, // Litigation (all branches)
  Ship, // Customs clearance, cases & container return
  Building2, // Iraqi company formation & foreign branches
  Stamp, // Companies, agencies & trademarks
  Landmark, // Commercial banking transactions
  FileSignature, // Contracts
  Home, // Real estate
  Handshake, // Conflict resolution
  IdCard, // Work permits for foreigners
  Languages, // Legal translation
  Calculator, // Final accounts
  FileCheck2, // Document attestation (MoFA)
];

export function ServicesSection() {
  const { t } = useLang();
  const services: Service[] = t.services.items.map((label, i) => ({
    label,
    Icon: icons[i],
  }));

  return (
    <section
      id="services"
      className="relative border-t border-primary/10 px-6 lg:px-24"
    >
      <div className="container mx-auto py-16 lg:py-24">
        <SectionHeader
          eyebrow={t.services.eyebrow}
          title={t.services.title}
        />

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => (
            <Reveal key={service.label} delay={(i % 3) * 0.08}>
              <ServiceCard {...service} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ label, Icon }: Service) {
  return (
    <div className="group flex h-full items-center gap-4 rounded-xl border border-primary/10 bg-white/70 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-secondary/60 hover:bg-white hover:shadow-[0_18px_40px_rgba(5,37,45,0.1)]">
      <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-lg border border-secondary/30 bg-primary/5 text-secondary transition-colors group-hover:bg-secondary group-hover:text-white">
        <Icon className="h-6 w-6" />
      </span>
      <p className="font-serif text-lg font-medium text-primary">{label}</p>
    </div>
  );
}

/** Shared gold pill — reused by the Clients section for company names. */
export const Chip = ({ text }: { text: string }) => (
  <div className="group flex items-center justify-center rounded-lg border border-primary/15 bg-white/70 px-4 py-4 text-center transition-colors duration-300 hover:border-secondary/60 hover:bg-secondary">
    <p className="font-serif text-base capitalize text-primary transition-colors group-hover:text-white md:text-lg">
      {text}
    </p>
  </div>
);
